import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { useNavigate } from 'react-router-dom';
import { Locate, Clock, Users, MapPin, Ticket, Tag } from 'lucide-react';
import { CategoryIcon } from './CategoryIcon';
import { useToast } from '../../contexts/ToastContext';
import { formatSpotsLeft } from '../../lib/capacity';
import { formatLiveState, formatSpan, isMultiDay, sessionsOrFallback, type EventSession } from '../../lib/eventSchedule';
import type { Coordinates } from '../../types';
import { eventPath, voucherPath } from '../../lib/paths';

// Set access token
const token = import.meta.env.VITE_MAPBOX_TOKEN;
if (!token && import.meta.env.DEV) {
  console.warn('[MapView] VITE_MAPBOX_TOKEN is not set — map tiles will not load');
}
mapboxgl.accessToken = token || '';

export interface MapEvent {
  id: string;
  slug?: string;
  title: string;
  venue_name: string;
  venue_lat: number;
  venue_lng: number;
  category: {
    name: string;
    icon: string;
  };
  start_time: string;
  end_time?: string | null;
  /** The periods the event runs. Absent on older queries — see sessionsOrFallback. */
  event_sessions?: EventSession[];
  participant_count: number;
  capacity: number | null;
  status?: string;
  audience?: 'everyone' | 'women' | 'men';
}

export interface MapVoucher {
  id: string;
  slug?: string;
  title: string;
  venue_name: string;
  venue_lat: number;
  venue_lng: number;
  discount_text: string;
  expires_at: string;
  business_name: string;
}

export interface MapViewProps {
  events: MapEvent[];
  vouchers?: MapVoucher[];
  userLocation: Coordinates | null;
  radiusKm?: number;
  onEventSelect?: (eventId: string) => void;
  className?: string;
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000);

  if (diffMins < 0) return 'Started';
  if (diffMins < 60) return `In ${diffMins}m`;
  if (diffMins < 1440) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Generate a GeoJSON circle for the radius overlay
function createGeoJSONCircle(center: [number, number], radiusKm: number, points = 64): GeoJSON.Feature {
  const coords: [number, number][] = [];
  const distanceX = radiusKm / (111.32 * Math.cos((center[1] * Math.PI) / 180));
  const distanceY = radiusKm / 110.574;

  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);
    coords.push([center[0] + x, center[1] + y]);
  }
  coords.push(coords[0]); // close ring

  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] },
    properties: {},
  };
}

// Convert vouchers to GeoJSON. Kept separate from events so the two sets get
// independent clusters and distinct colours on the map.
function vouchersToGeoJSON(vouchers: MapVoucher[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: vouchers.map((v) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [v.venue_lng, v.venue_lat] },
      properties: {
        id: v.id,
        title: v.title,
        venue_name: v.venue_name,
        discount_text: v.discount_text,
        expires_at: v.expires_at,
        business_name: v.business_name,
      },
    })),
  };
}

// Convert events to GeoJSON for clustering
function eventsToGeoJSON(events: MapEvent[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: events.map((event) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [event.venue_lng, event.venue_lat],
      },
      properties: {
        id: event.id,
        title: event.title,
        venue_name: event.venue_name,
        category_name: event.category.name,
        category_icon: event.category.icon,
        start_time: event.start_time,
        participant_count: event.participant_count,
        capacity: event.capacity,
        emoji: getEmojiForCategory(event.category.icon),
        ended: event.status === 'expired',
      },
    })),
  };
}

export function MapView({ events, vouchers = [], userLocation, radiusKm, className }: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<MapEvent | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<MapVoucher | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Default center (Dubai) if no user location
  const defaultCenter: [number, number] = [55.2708, 25.2048];

  // Find me / recenter on user location
  const handleFindMe = useCallback(() => {
    if (!map.current || !isLoaded) return;

    if (userLocation) {
      setIsLocating(true);
      map.current.flyTo({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: 15,
        duration: 1000,
      });
      setTimeout(() => setIsLocating(false), 1000);
      return;
    }

    if (!navigator.geolocation) {
      showToast('Location not supported on this device', 'error');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        map.current?.flyTo({
          center: [position.coords.longitude, position.coords.latitude],
          zoom: 15,
          duration: 1000,
        });
        setTimeout(() => setIsLocating(false), 1000);
      },
      (err) => {
        setIsLocating(false);
        // Surface a clear reason so users know why the button didn't "work"
        const msg =
          err.code === err.PERMISSION_DENIED
            ? 'Location access denied. Enable it in your browser settings.'
            : err.code === err.POSITION_UNAVAILABLE
            ? 'Location unavailable — try again in a moment.'
            : err.code === err.TIMEOUT
            ? "Couldn't get your location — check GPS / connection."
            : 'Unable to get your location.';
        showToast(msg, 'error');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [userLocation, isLoaded, showToast]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Clean up any existing map (handles React StrictMode double-mount)
    if (map.current) {
      map.current.remove();
      map.current = null;
      setIsLoaded(false);
    }

    const center: [number, number] = userLocation
      ? [userLocation.longitude, userLocation.latitude]
      : defaultCenter;

    const m = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center,
      zoom: 13,
      attributionControl: false,
    });

    map.current = m;

    m.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      'bottom-left'
    );

    m.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'bottom-right'
    );

    m.on('load', () => {
      // Force resize to ensure canvas matches container after layout settles
      m.resize();
      setIsLoaded(true);
    });

    // Also resize after a short delay to handle late layout shifts
    const resizeTimer = setTimeout(() => {
      if (map.current) map.current.resize();
    }, 200);

    return () => {
      clearTimeout(resizeTimer);
      m.remove();
      map.current = null;
    };
  }, []);

  // Update center when user location changes
  useEffect(() => {
    if (!map.current || !userLocation || !isLoaded) return;

    map.current.flyTo({
      center: [userLocation.longitude, userLocation.latitude],
      zoom: 15,
      duration: 1000,
    });
  }, [userLocation, isLoaded]);

  // Add user location marker
  useEffect(() => {
    if (!map.current || !userLocation || !isLoaded) return;

    const userEl = document.createElement('div');
    userEl.innerHTML = `
      <div class="relative flex items-center justify-center">
        <div class="absolute w-8 h-8 rounded-full bg-blue-500/20 animate-ping"></div>
        <div class="absolute w-5 h-5 rounded-full bg-blue-500/15"></div>
        <div class="relative w-3.5 h-3.5 rounded-full bg-blue-500 border-2 border-white shadow-lg"></div>
      </div>
    `;

    const userMarker = new mapboxgl.Marker({ element: userEl })
      .setLngLat([userLocation.longitude, userLocation.latitude])
      .addTo(map.current);

    return () => {
      userMarker.remove();
    };
  }, [userLocation, isLoaded]);

  // Radius circle
  useEffect(() => {
    if (!map.current || !isLoaded || !userLocation || !radiusKm) return;

    const sourceId = 'radius-circle';
    const fillId = 'radius-fill';
    const strokeId = 'radius-stroke';
    const m = map.current;

    const circleGeoJSON = createGeoJSONCircle(
      [userLocation.longitude, userLocation.latitude],
      radiusKm
    );

    if (m.getSource(sourceId)) {
      (m.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(circleGeoJSON as any);
    } else {
      m.addSource(sourceId, {
        type: 'geojson',
        data: circleGeoJSON as any,
      });

      m.addLayer({
        id: fillId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': '#845EF7',
          'fill-opacity': 0.06,
        },
      });

      m.addLayer({
        id: strokeId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#845EF7',
          'line-width': 1.5,
          'line-opacity': 0.3,
          'line-dasharray': [4, 4],
        },
      });
    }

    return () => {
      // Layers cleaned up on unmount via map.remove()
    };
  }, [userLocation, radiusKm, isLoaded]);

  // Clustered event markers
  useEffect(() => {
    if (!map.current || !isLoaded) return;

    const m = map.current;
    const sourceId = 'events-source';

    const geojson = eventsToGeoJSON(events);

    // Update existing source or create new one
    if (m.getSource(sourceId)) {
      (m.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geojson);
    } else {
      m.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Cluster circles
      m.addLayer({
        id: 'clusters',
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#845EF7',  // purple for small clusters
            5,
            '#FF6B6B',  // coral for medium
            15,
            '#E64545',  // darker coral for large
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,   // small
            5, 25, // medium
            15, 32, // large
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Cluster count labels
      m.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 13,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Individual event markers — background circle
      m.addLayer({
        id: 'unclustered-circle',
        type: 'circle',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 18,
          'circle-color': ['case', ['==', ['get', 'ended'], true], '#9CA3AF', '#FF6B6B'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Individual event markers — emoji on top
      m.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': ['get', 'emoji'],
          'text-size': 16,
          'text-allow-overlap': true,
        },
      });

      // Click cluster to zoom in
      m.on('click', 'clusters', (e) => {
        const features = m.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!features.length) return;
        const clusterId = features[0].properties?.cluster_id;
        (m.getSource(sourceId) as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err || zoom == null) return;
            const geometry = features[0].geometry;
            if (geometry.type !== 'Point') return;
            m.easeTo({
              center: geometry.coordinates as [number, number],
              zoom,
            });
          }
        );
      });

      // Click unclustered marker to show preview
      m.on('click', 'unclustered-point', (e) => {
        const features = m.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] });
        if (!features.length) return;
        const props = features[0].properties;
        if (!props) return;

        const event = events.find((ev) => ev.id === props.id);
        if (event) {
          setSelectedEvent(event);
        }
      });

      // Circle layer also opens preview
      m.on('click', 'unclustered-circle', (e) => {
        const features = m.queryRenderedFeatures(e.point, { layers: ['unclustered-circle'] });
        if (!features.length) return;
        const props = features[0].properties;
        if (!props) return;
        const event = events.find((ev) => ev.id === props.id);
        if (event) setSelectedEvent(event);
      });

      // Pointer cursor on interactive layers
      m.on('mouseenter', 'clusters', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'clusters', () => { m.getCanvas().style.cursor = ''; });
      m.on('mouseenter', 'unclustered-point', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'unclustered-point', () => { m.getCanvas().style.cursor = ''; });
      m.on('mouseenter', 'unclustered-circle', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'unclustered-circle', () => { m.getCanvas().style.cursor = ''; });
    }

    // Fit bounds to show all markers
    if (events.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      if (userLocation) {
        bounds.extend([userLocation.longitude, userLocation.latitude]);
      }
      events.forEach((event) => {
        bounds.extend([event.venue_lng, event.venue_lat]);
      });
      m.fitBounds(bounds, {
        padding: 60,
        maxZoom: 15,
        duration: 500,
      });
    }
  }, [events, isLoaded, userLocation]);

  // Voucher markers — separate source so the two pin types cluster and colour
  // independently. Vouchers use a purple tile so users can tell at a glance
  // which pins are deals vs which are events.
  useEffect(() => {
    if (!map.current || !isLoaded) return;

    const m = map.current;
    const sourceId = 'vouchers-source';
    const geojson = vouchersToGeoJSON(vouchers);

    if (m.getSource(sourceId)) {
      (m.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geojson);
    } else {
      m.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      m.addLayer({
        id: 'voucher-clusters',
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#845EF7',
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            18,
            5, 22,
            15, 28,
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.9,
        },
      });

      m.addLayer({
        id: 'voucher-cluster-count',
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: { 'text-color': '#ffffff' },
      });

      m.addLayer({
        id: 'voucher-unclustered-circle',
        type: 'circle',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 16,
          'circle-color': '#845EF7',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      m.addLayer({
        id: 'voucher-unclustered-symbol',
        type: 'symbol',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': '%',
          'text-size': 16,
          'text-allow-overlap': true,
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        },
        paint: { 'text-color': '#ffffff' },
      });

      m.on('click', 'voucher-clusters', (e) => {
        const features = m.queryRenderedFeatures(e.point, { layers: ['voucher-clusters'] });
        if (!features.length) return;
        const clusterId = features[0].properties?.cluster_id;
        (m.getSource(sourceId) as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err || zoom == null) return;
            const geometry = features[0].geometry;
            if (geometry.type !== 'Point') return;
            m.easeTo({ center: geometry.coordinates as [number, number], zoom });
          },
        );
      });

      const openVoucherPreview = (e: mapboxgl.MapMouseEvent) => {
        const features = m.queryRenderedFeatures(e.point, {
          layers: ['voucher-unclustered-circle', 'voucher-unclustered-symbol'],
        });
        if (!features.length) return;
        const props = features[0].properties;
        if (!props) return;
        const voucher = vouchers.find((v) => v.id === props.id);
        if (voucher) {
          setSelectedVoucher(voucher);
          setSelectedEvent(null);
        }
      };
      m.on('click', 'voucher-unclustered-circle', openVoucherPreview);
      m.on('click', 'voucher-unclustered-symbol', openVoucherPreview);

      m.on('mouseenter', 'voucher-clusters', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'voucher-clusters', () => { m.getCanvas().style.cursor = ''; });
      m.on('mouseenter', 'voucher-unclustered-circle', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'voucher-unclustered-circle', () => { m.getCanvas().style.cursor = ''; });
    }
  }, [vouchers, isLoaded]);

  // Close preview when clicking the map background
  useEffect(() => {
    if (!map.current || !isLoaded) return;
    const m = map.current;

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      const features = m.queryRenderedFeatures(e.point, {
        layers: [
          'clusters', 'unclustered-point', 'unclustered-circle',
          'voucher-clusters', 'voucher-unclustered-circle', 'voucher-unclustered-symbol',
        ],
      });
      if (features.length === 0) {
        setSelectedEvent(null);
        setSelectedVoucher(null);
      }
    };

    m.on('click', handleClick);
    return () => {
      m.off('click', handleClick);
    };
  }, [isLoaded]);

  return (
    <div className={className}>
      <div ref={mapContainer} className="w-full h-full" />

      {/* Find Me button */}
      {isLoaded && (
        <button
          onClick={handleFindMe}
          disabled={isLocating}
          className="absolute bottom-28 right-2 z-10 w-10 h-10 bg-surface rounded-lg shadow-lg border border-border flex items-center justify-center hover:bg-background active:bg-background transition-colors disabled:opacity-50"
          aria-label="Find my location"
        >
          <Locate
            className={`h-5 w-5 text-text-muted ${isLocating ? 'animate-pulse text-coral' : ''}`}
          />
        </button>
      )}

      {/* Event Preview Card */}
      {selectedEvent && (
        <div
          ref={previewRef}
          className="absolute bottom-20 left-3 right-3 z-20 animate-slide-up"
        >
          <EventPreviewCard
            event={selectedEvent}
            onTap={() => navigate(eventPath(selectedEvent))}
            onClose={() => setSelectedEvent(null)}
          />
        </div>
      )}

      {/* Voucher Preview Card */}
      {selectedVoucher && (
        <div className="absolute bottom-20 left-3 right-3 z-20 animate-slide-up">
          <VoucherPreviewCard
            voucher={selectedVoucher}
            onTap={() => navigate(voucherPath(selectedVoucher))}
            onClose={() => setSelectedVoucher(null)}
          />
        </div>
      )}

      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <div className="animate-spin w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
}

// Event Preview Card shown when tapping a marker
function EventPreviewCard({
  event,
  onTap,
  onClose,
}: {
  event: MapEvent;
  onTap: () => void;
  onClose: () => void;
}) {
  const isEnded = event.status === 'expired';
  const previewSessions = sessionsOrFallback(event.event_sessions, event.start_time, event.end_time);
  const previewIsMultiDay = isMultiDay(previewSessions);

  return (
    <div
      onClick={onTap}
      className="bg-surface rounded-2xl shadow-xl border border-border p-4 cursor-pointer active:scale-[0.98] transition-transform"
    >
      <div className="flex gap-3">
        {/* Category icon */}
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-coral to-purple flex items-center justify-center flex-shrink-0">
          <CategoryIcon icon={event.category.icon} size="lg" className="text-white" />
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-text text-sm truncate">{event.title}</h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-text-light hover:text-text-muted text-lg leading-none flex-shrink-0 -mt-0.5"
            >
              &times;
            </button>
          </div>

          <div className="flex items-center gap-1 text-xs text-text-muted mt-0.5">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{event.venue_name}</span>
          </div>

          <div className="flex items-center gap-3 mt-2">
            <div className={`flex items-center gap-1 text-xs font-medium ${isEnded ? 'text-text-muted' : 'text-coral'}`}>
              <Clock className="h-3 w-3" />
              {isEnded
                ? 'Ended'
                : previewIsMultiDay
                  ? `${formatSpan(previewSessions)} · ${formatLiveState(previewSessions)}`
                  : formatTime(event.start_time)}
            </div>
            {!isEnded && (
              <div className="flex items-center gap-1 text-xs text-text-muted">
                <Users className="h-3 w-3" />
                {formatSpotsLeft(event.capacity, event.participant_count)}
              </div>
            )}
            <span className="ml-auto flex items-center gap-1">
              {event.audience === 'women' && (
                <span className="text-[10px] font-medium text-white bg-purple px-2 py-0.5 rounded-full">
                  Women only
                </span>
              )}
              <span className="text-[10px] font-medium text-purple bg-purple/10 px-2 py-0.5 rounded-full">
                {event.category.name}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Voucher Preview Card — shown when tapping a voucher pin
function VoucherPreviewCard({
  voucher,
  onTap,
  onClose,
}: {
  voucher: MapVoucher;
  onTap: () => void;
  onClose: () => void;
}) {
  const daysLeft = Math.max(
    0,
    Math.floor((new Date(voucher.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );

  return (
    <div
      onClick={onTap}
      className="bg-surface rounded-2xl shadow-xl border border-border p-4 cursor-pointer active:scale-[0.98] transition-transform"
    >
      <div className="flex gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple to-coral flex items-center justify-center flex-shrink-0">
          <Ticket className="h-6 w-6 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-text text-sm truncate">{voucher.title}</h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-text-light hover:text-text-muted text-lg leading-none flex-shrink-0 -mt-0.5"
            >
              &times;
            </button>
          </div>

          <div className="flex items-center gap-1 text-xs text-text-muted mt-0.5">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{voucher.venue_name}</span>
          </div>

          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1 text-xs font-bold text-purple">
              <Tag className="h-3 w-3" />
              {voucher.discount_text}
            </div>
            <div className="text-xs text-text-muted">
              {daysLeft === 0 ? 'Last day' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`}
            </div>
            <span className="ml-auto text-[10px] font-medium text-coral bg-coral/10 px-2 py-0.5 rounded-full truncate max-w-[40%]">
              {voucher.business_name}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to get emoji for category icons
function getEmojiForCategory(icon: string): string {
  const emojiMap: Record<string, string> = {
    Coffee: '☕',
    Utensils: '🍽️',
    Dumbbell: '💪',
    Trophy: '🏆',
    TreePine: '🌲',
    Heart: '❤️',
    Film: '🎬',
    Gamepad2: '🎮',
    Palette: '🎨',
    BookOpen: '📚',
    PartyPopper: '🎉',
    Dog: '🐕',
    Baby: '👶',
    Ellipsis: '📍',
    Calendar: '📅',
    Umbrella: '🏖️',
    Dices: '🎲',
    Bike: '🚴',
    Mountain: '⛰️',
    Languages: '🗣️',
    Music: '🎵',
    Camera: '📷',
    PersonStanding: '🏃',
    ShoppingBag: '🛍️',
    Footprints: '👣',
  };
  return emojiMap[icon] || '📍';
}

export default MapView;
